import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const IconHouse = ({ size = 24, color = 'currentColor', opacity = 1 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{opacity}}>
    <path d="M3 10.5L12 3l9 7.5V21a1 1 0 01-1 1H4a1 1 0 01-1-1V10.5z"/>
    <path d="M9 22V12h6v10"/>
  </svg>
);

const IconShield = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l8 3v6c0 5-3.5 9-8 11C7.5 20 4 16 4 11V5l8-3z"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const IconDoc = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2"/>
    <path d="M8 7h8M8 11h8M8 15h5"/>
  </svg>
);

const IconEscrow = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="M12 6v1.5m0 9V18m3-5.5c0-1.4-1.3-2.5-3-2.5s-3 1.1-3 2.5 1.3 2.5 3 2.5 3 1.1 3 2.5-1.3 2.5-3 2.5-3-1.1-3-2.5"/>
  </svg>
);

const IconPin = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8.7 2 6 4.7 6 8c0 4.5 6 14 6 14s6-9.5 6-14c0-3.3-2.7-6-6-6z"/>
    <circle cx="12" cy="8" r="2"/>
  </svg>
);

const IconCheck = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12l5 5L20 7"/>
  </svg>
);

const IconStar = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#d4a843" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const RealEstate = () => {
  useEffect(() => {
    const nav = document.getElementById('re-nav');
    const onScroll = () => nav?.classList.toggle('scrolled', window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const reveals = document.querySelectorAll('.re-reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.12 });
    reveals.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <title>Real Estate Photography in Denver for Agents &amp; Brokerages | FrameFinder</title>
        <meta name="description" content="Find vetted real estate photographers in Denver. Fast turnaround, consistent quality, and escrow-backed bookings. Built for listing agents and brokerages." />
        <link rel="canonical" href="https://www.framefinder.io/real-estate" />
      </Helmet>
      <style>{`
        .re-wrap *, .re-wrap *::before, .re-wrap *::after { box-sizing: border-box; }
        .re-wrap {
          --ink: #0e0e0e; --ink-soft: #5a5a5a; --ink-faint: #a0a0a0;
          --paper: #f7f5f2; --white: #ffffff; --accent: #d4a843;
          --accent-soft: #f5ead0; --accent-deep: #8a6a1a;
          --border: #e4e1db; --border-deep: #c8c3bb;
          --shadow-sm: 0 1px 4px rgba(14,14,14,0.06);
          --shadow-md: 0 4px 20px rgba(14,14,14,0.08);
          --shadow-lg: 0 12px 48px rgba(14,14,14,0.14);
          --r-sm: 8px; --r-md: 14px; --r-lg: 24px; --r-pill: 100px;
          font-family: 'DM Sans', sans-serif;
          background: var(--paper);
          color: var(--ink);
          -webkit-font-smoothing: antialiased;
          line-height: 1.5;
          overflow-x: hidden;
        }
        /* NAV */
        .re-nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 100;
          padding: 0 40px; height: 64px;
          display: flex; align-items: center; justify-content: space-between;
          background: rgba(247,245,242,0.88);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid transparent; transition: border-color 0.3s;
        }
        .re-nav.scrolled { border-bottom-color: var(--border); }
        .re-nav-logo { font-family: 'Instrument Serif', serif; font-size: 22px; color: var(--ink); text-decoration: none; letter-spacing: -0.3px; }
        .re-nav-right { display: flex; align-items: center; gap: 12px; }
        .re-nav-link { font-size: 13px; font-weight: 500; color: var(--ink-soft); text-decoration: none; transition: color 0.15s; }
        .re-nav-link:hover { color: var(--ink); }
        .re-btn { display: inline-flex; align-items: center; gap: 6px; font-family: 'DM Sans', sans-serif; font-weight: 500; border: none; cursor: pointer; text-decoration: none; border-radius: var(--r-pill); transition: transform 0.15s, box-shadow 0.15s, opacity 0.15s; white-space: nowrap; }
        .re-btn:hover { transform: translateY(-1px); }
        .re-btn-primary { background: var(--ink); color: var(--white); box-shadow: 0 2px 12px rgba(14,14,14,0.18); font-size: 14px; padding: 10px 22px; }
        .re-btn-primary:hover { box-shadow: 0 6px 20px rgba(14,14,14,0.26); }
        .re-btn-ghost { background: var(--white); color: var(--ink); border: 1px solid var(--border); font-size: 14px; padding: 10px 22px; }
        .re-btn-ghost:hover { background: var(--paper); }
        .re-btn-accent { background: var(--accent); color: var(--ink); font-size: 15px; padding: 14px 32px; }
        .re-btn-accent:hover { opacity: 0.9; }
        /* HERO */
        .re-hero { min-height: 100vh; background: var(--ink); display: flex; flex-direction: column; justify-content: center; position: relative; overflow: hidden; padding: 120px 40px 80px; }
        .re-hero::before { content: ''; position: absolute; right: -200px; top: -200px; width: 900px; height: 900px; background: radial-gradient(circle, rgba(212,168,67,0.09) 0%, transparent 65%); pointer-events: none; }
        .re-hero::after { content: ''; position: absolute; left: -100px; bottom: -100px; width: 600px; height: 600px; background: radial-gradient(circle, rgba(212,168,67,0.05) 0%, transparent 65%); pointer-events: none; }
        .re-hero-inner { max-width: 1120px; margin: 0 auto; width: 100%; position: relative; z-index: 1; display: grid; grid-template-columns: 1fr 420px; gap: 80px; align-items: center; }
        .re-hero-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 24px; opacity: 0; animation: reFadeUp 0.6s ease 0.1s both; }
        .re-eyebrow-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }
        .re-hero-title { font-family: 'Instrument Serif', serif; font-weight: 400; font-size: clamp(44px, 5.5vw, 72px); line-height: 1.0; letter-spacing: -2px; color: var(--white); margin-bottom: 24px; opacity: 0; animation: reFadeUp 0.6s ease 0.2s both; }
        .re-hero-title em { font-style: italic; color: rgba(255,255,255,0.38); }
        .re-hero-subtitle { font-size: 17px; line-height: 1.65; color: rgba(255,255,255,0.55); max-width: 500px; margin-bottom: 40px; opacity: 0; animation: reFadeUp 0.6s ease 0.3s both; }
        .re-hero-ctas { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; opacity: 0; animation: reFadeUp 0.6s ease 0.4s both; }
        .re-btn-hero-primary { background: var(--white); color: var(--ink); font-size: 15px; padding: 14px 30px; box-shadow: 0 4px 20px rgba(0,0,0,0.3); }
        .re-btn-hero-ghost { background: transparent; color: rgba(255,255,255,0.65); border: 1px solid rgba(255,255,255,0.18); font-size: 15px; padding: 14px 30px; }
        .re-btn-hero-ghost:hover { border-color: rgba(255,255,255,0.4); color: var(--white); }
        .re-hero-trust { margin-top: 48px; display: flex; align-items: center; gap: 20px; opacity: 0; animation: reFadeUp 0.6s ease 0.5s both; }
        .re-trust-item { display: flex; align-items: center; gap: 8px; font-size: 13px; color: rgba(255,255,255,0.4); }
        .re-trust-icon { color: var(--accent); font-size: 14px; }
        .re-trust-divider { width: 1px; height: 16px; background: rgba(255,255,255,0.12); }
        /* Hero visual */
        .re-hero-visual { position: relative; height: 480px; opacity: 0; animation: reFadeUp 0.7s ease 0.35s both; }
        .re-listing-card { position: absolute; background: var(--white); border-radius: var(--r-lg); overflow: hidden; box-shadow: var(--shadow-lg); }
        .re-card-main { width: 340px; right: 0; top: 0; }
        .re-card-secondary { width: 240px; left: 0; bottom: 60px; box-shadow: 0 8px 40px rgba(0,0,0,0.25); }
        .re-listing-img { height: 180px; background: linear-gradient(145deg, #c5d0d8 0%, #a8b8c5 40%, #8fa5b5 100%); display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; }
        .re-listing-img::before { content: ''; position: absolute; inset: 0; background: linear-gradient(180deg, transparent 50%, rgba(0,0,0,0.2) 100%); }
        .re-listing-img-icon { font-size: 40px; opacity: 0.5; position: relative; z-index: 1; }
        .re-listing-img-sm { height: 120px; background: linear-gradient(145deg, #d4c8b8 0%, #c0ae96 40%, #a89070 100%); }
        .re-listing-body { padding: 16px 18px; }
        .re-listing-body-sm { padding: 12px 14px; }
        .re-listing-address { font-size: 13px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
        .re-listing-address-sm { font-size: 12px; }
        .re-listing-meta { font-size: 11px; color: var(--ink-faint); display: flex; align-items: center; gap: 6px; }
        .re-listing-footer { padding: 12px 18px; border-top: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
        .re-listing-footer-sm { padding: 10px 14px; }
        .re-photographer-mini { display: flex; align-items: center; gap: 8px; }
        .re-photographer-avatar { width: 28px; height: 28px; border-radius: 50%; background: linear-gradient(135deg, var(--accent-soft), var(--accent)); display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; color: var(--accent-deep); flex-shrink: 0; }
        .re-photographer-name { font-size: 12px; font-weight: 600; color: var(--ink); }
        .re-photographer-tier { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: var(--r-pill); background: var(--accent-soft); color: var(--accent-deep); border: 1px solid #e8d49a; }
        .re-status-badge { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: var(--r-pill); background: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }
        .re-status-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; }
        .re-float-pill { position: absolute; top: -20px; left: 50%; transform: translateX(-50%); background: var(--ink); color: var(--white); font-size: 12px; font-weight: 600; padding: 8px 18px; border-radius: var(--r-pill); white-space: nowrap; box-shadow: 0 4px 20px rgba(0,0,0,0.3); border: 1px solid rgba(255,255,255,0.1); animation: reFloatPill 3s ease-in-out infinite; }
        .re-float-pill-accent { background: var(--accent); color: var(--ink); border-color: transparent; }
        @keyframes reFloatPill { 0%, 100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-6px); } }
        /* SECTIONS */
        .re-section { padding: 100px 40px; }
        .re-section-inner { max-width: 1120px; margin: 0 auto; }
        .re-section-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--ink-faint); margin-bottom: 12px; }
        .re-section-title { font-family: 'Instrument Serif', serif; font-weight: 400; font-size: clamp(32px, 3.5vw, 48px); letter-spacing: -1.5px; line-height: 1.05; color: var(--ink); margin-bottom: 16px; }
        .re-section-title em { font-style: italic; color: var(--ink-soft); }
        .re-section-desc { font-size: 16px; color: var(--ink-soft); max-width: 540px; line-height: 1.7; }
        /* PAIN */
        .re-pain-section { background: var(--white); border-top: 1px solid var(--border); border-bottom: 1px solid var(--border); padding: 80px 40px; }
        .re-pain-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; margin-top: 56px; }
        .re-pain-item { background: var(--white); padding: 40px 36px; position: relative; }
        .re-pain-number { font-family: 'Instrument Serif', serif; font-size: 48px; letter-spacing: -2px; color: var(--border-deep); line-height: 1; margin-bottom: 16px; }
        .re-pain-title { font-size: 17px; font-weight: 600; color: var(--ink); margin-bottom: 10px; letter-spacing: -0.2px; }
        .re-pain-body { font-size: 14px; color: var(--ink-soft); line-height: 1.7; }
        /* HOW IT WORKS */
        .re-how-section { padding: 100px 40px; }
        .re-steps-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; margin-top: 64px; }
        .re-step-card { position: relative; }
        .re-step-num { width: 56px; height: 56px; border-radius: 50%; background: var(--paper); border: 1px solid var(--border); display: flex; align-items: center; justify-content: center; font-family: 'Instrument Serif', serif; font-size: 22px; color: var(--ink-soft); margin-bottom: 20px; }
        .re-step-num-active { background: var(--ink); border-color: var(--ink); color: var(--white); }
        .re-step-title { font-size: 15px; font-weight: 600; color: var(--ink); margin-bottom: 8px; letter-spacing: -0.2px; }
        .re-step-body { font-size: 13px; color: var(--ink-soft); line-height: 1.65; }
        /* WHY */
        .re-why-section { background: var(--ink); padding: 100px 40px; position: relative; overflow: hidden; }
        .re-why-section::before { content: ''; position: absolute; right: -200px; top: -200px; width: 800px; height: 800px; background: radial-gradient(circle, rgba(212,168,67,0.07) 0%, transparent 65%); pointer-events: none; }
        .re-why-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 64px; align-items: start; position: relative; z-index: 1; }
        .re-why-left .re-section-title { color: var(--white); }
        .re-why-left .re-section-title em { color: rgba(255,255,255,0.3); }
        .re-why-left .re-section-desc { color: rgba(255,255,255,0.5); }
        .re-why-left .re-section-eyebrow { color: rgba(255,255,255,0.25); }
        .re-why-features { display: flex; flex-direction: column; gap: 2px; margin-top: 8px; }
        .re-why-feature { display: flex; align-items: flex-start; gap: 16px; padding: 24px; border-radius: var(--r-md); border: 1px solid transparent; transition: background 0.2s, border-color 0.2s; }
        .re-why-feature:hover { background: rgba(255,255,255,0.04); border-color: rgba(255,255,255,0.08); }
        .re-why-feature-icon { width: 40px; height: 40px; border-radius: var(--r-sm); background: rgba(212,168,67,0.12); display: flex; align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0; }
        .re-why-feature-title { font-size: 15px; font-weight: 600; color: var(--white); margin-bottom: 6px; letter-spacing: -0.2px; }
        .re-why-feature-body { font-size: 13px; color: rgba(255,255,255,0.45); line-height: 1.65; }
        /* PHOTOGRAPHERS */
        .re-photographers-section { padding: 100px 40px; background: var(--white); border-top: 1px solid var(--border); }
        .re-photo-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 56px; }
        .re-photo-card { background: var(--paper); border: 1px solid var(--border); border-radius: var(--r-lg); overflow: hidden; transition: transform 0.2s, box-shadow 0.2s; }
        .re-photo-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); }
        .re-photo-card-img { height: 160px; display: flex; align-items: center; justify-content: center; font-size: 40px; }
        .re-photo-card-img-1 { background: linear-gradient(135deg, #ddd4c4 0%, #c4b8a4 100%); }
        .re-photo-card-img-2 { background: linear-gradient(135deg, #c4d0d8 0%, #a8bac8 100%); }
        .re-photo-card-img-3 { background: linear-gradient(135deg, #d0c8d8 0%, #b8b0c8 100%); }
        .re-photo-card-body { padding: 18px 20px; }
        .re-photo-card-name { font-size: 15px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
        .re-photo-card-spec { font-size: 12px; color: var(--ink-faint); display: flex; align-items: center; gap: 6px; margin-bottom: 12px; }
        .re-photo-card-tags { display: flex; gap: 6px; flex-wrap: wrap; }
        .re-tag { font-size: 11px; font-weight: 500; padding: 4px 10px; border-radius: var(--r-pill); background: var(--white); border: 1px solid var(--border); color: var(--ink-soft); }
        .re-photo-card-footer { padding: 14px 20px; border-top: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
        .re-photo-card-rate { font-size: 14px; font-weight: 700; color: var(--ink); }
        .re-avail { display: flex; align-items: center; gap: 5px; font-size: 11px; font-weight: 600; color: #166534; }
        .re-avail-dot { width: 6px; height: 6px; border-radius: 50%; background: #4ade80; }
        /* CTA */
        .re-cta-section { padding: 100px 40px; background: var(--ink); position: relative; overflow: hidden; }
        .re-cta-section::before { content: ''; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 1000px; height: 600px; background: radial-gradient(ellipse, rgba(212,168,67,0.08) 0%, transparent 65%); pointer-events: none; }
        .re-cta-inner { max-width: 680px; margin: 0 auto; text-align: center; position: relative; z-index: 1; }
        .re-cta-eyebrow { font-size: 11px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: var(--accent); margin-bottom: 20px; }
        .re-cta-title { font-family: 'Instrument Serif', serif; font-weight: 400; font-size: clamp(36px, 4vw, 56px); letter-spacing: -2px; line-height: 1.0; color: var(--white); margin-bottom: 20px; }
        .re-cta-title em { font-style: italic; color: rgba(255,255,255,0.3); }
        .re-cta-body { font-size: 16px; color: rgba(255,255,255,0.5); line-height: 1.7; margin-bottom: 40px; }
        .re-cta-acts { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; }
        .re-cta-disclaimer { font-size: 12px; color: rgba(255,255,255,0.25); margin-top: 16px; }
        /* FOOTER */
        .re-footer { background: #080808; padding: 40px; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 16px; border-top: 1px solid rgba(255,255,255,0.06); }
        .re-footer-logo { font-family: 'Instrument Serif', serif; font-size: 20px; color: var(--white); text-decoration: none; }
        .re-footer-links { display: flex; gap: 24px; }
        .re-footer-link { font-size: 13px; color: rgba(255,255,255,0.3); text-decoration: none; transition: color 0.15s; }
        .re-footer-link:hover { color: rgba(255,255,255,0.7); }
        .re-footer-copy { font-size: 12px; color: rgba(255,255,255,0.2); }
        /* REVEAL */
        .re-reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.6s ease, transform 0.6s ease; }
        .re-reveal.visible { opacity: 1; transform: translateY(0); }
        .re-reveal-d1 { transition-delay: 0.1s; }
        .re-reveal-d2 { transition-delay: 0.2s; }
        .re-reveal-d3 { transition-delay: 0.3s; }
        @keyframes reFadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        /* RESPONSIVE */
        @media (max-width: 960px) {
          .re-hero-inner { grid-template-columns: 1fr; gap: 60px; }
          .re-hero-visual { height: 360px; }
          .re-card-main { width: 280px; }
          .re-card-secondary { width: 200px; }
          .re-steps-grid { grid-template-columns: repeat(2, 1fr); }
          .re-why-grid { grid-template-columns: 1fr; gap: 48px; }
          .re-photo-grid { grid-template-columns: repeat(2, 1fr); }
          .re-pain-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 600px) {
          .re-nav { padding: 0 20px; }
          .re-nav-link { display: none; }
          .re-section, .re-pain-section, .re-how-section, .re-why-section,
          .re-photographers-section, .re-cta-section { padding: 64px 20px; }
          .re-hero { padding: 100px 20px 64px; }
          .re-steps-grid, .re-photo-grid { grid-template-columns: 1fr; }
          .re-hero-visual { display: none; }
          .re-footer { padding: 32px 20px; flex-direction: column; align-items: flex-start; }
        }
      `}</style>

      <div className="re-wrap">
        {/* NAV */}
        <nav className="re-nav" id="re-nav">
          <Link className="re-nav-logo" to="/">FrameFinder</Link>
          <div className="re-nav-right">
            <Link to="/photographers" className="re-nav-link">Browse photographers</Link>
            <Link to="/login" className="re-btn re-btn-ghost" style={{fontSize:'13px',padding:'8px 18px'}}>Log in</Link>
            <Link to="/signup" className="re-btn re-btn-primary" style={{fontSize:'13px',padding:'8px 18px'}}>Post a listing shoot →</Link>
          </div>
        </nav>

        {/* HERO */}
        <section className="re-hero">
          <div className="re-hero-inner">
            <div>
              <div className="re-hero-eyebrow">
                <span className="re-eyebrow-dot"></span>
                Real estate photography · Denver, CO
              </div>
              <h1 className="re-hero-title">Reliable Real Estate Photography<br />for <em>Denver Listing Agents</em></h1>
              <p className="re-hero-subtitle">Post your shoot, get matched with a vetted Denver photographer, and have your listing photos in hand — without the back-and-forth that costs you days.</p>
              <div className="re-hero-ctas">
                <Link to="/signup" className="re-btn re-btn-hero-primary re-btn">Post a listing shoot →</Link>
                <Link to="/photographers" className="re-btn re-btn-hero-ghost re-btn">Browse photographers</Link>
              </div>
              <div className="re-hero-trust">
                <div className="re-trust-item"><span className="re-trust-icon"><IconCheck /></span>Vetted photographers</div>
                <div className="re-trust-divider"></div>
                <div className="re-trust-item"><span className="re-trust-icon"><IconCheck /></span>Escrow payments</div>
                <div className="re-trust-divider"></div>
                <div className="re-trust-item"><span className="re-trust-icon"><IconCheck /></span>Free during beta</div>
              </div>
            </div>
            <div className="re-hero-visual">
              <div className="re-listing-card re-card-main">
                <div className="re-listing-img"><IconHouse size={40} color="#8fa5b5" opacity={0.7} /></div>
                <div className="re-listing-body">
                  <div className="re-listing-address">2847 Larimer St, Denver</div>
                  <div className="re-listing-meta">4 bed · 3 bath · 2,400 sq ft</div>
                </div>
                <div className="re-listing-footer">
                  <div className="re-photographer-mini">
                    <div className="re-photographer-avatar">MT</div>
                    <div><div className="re-photographer-name">Marcus Torres</div></div>
                  </div>
                  <div className="re-status-badge"><div className="re-status-dot"></div>Confirmed</div>
                </div>
              </div>
              <div className="re-listing-card re-card-secondary" style={{position:'relative'}}>
                <div className="re-float-pill re-float-pill-accent">↑ Live in 48 hrs</div>
                <div className="re-listing-img re-listing-img-sm"><IconHouse size={28} color="#a89070" opacity={0.6} /></div>
                <div className="re-listing-body re-listing-body-sm">
                  <div className="re-listing-address re-listing-address-sm">1519 Blake St, Denver</div>
                  <div className="re-listing-meta" style={{fontSize:'10px'}}>3 bed · 2 bath</div>
                </div>
                <div className="re-listing-footer re-listing-footer-sm">
                  <span className="re-photographer-tier">◆ Established</span>
                  <span style={{fontSize:'12px',color:'var(--ink-faint)'}}>$165/hr</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PAIN POINTS */}
        <section className="re-pain-section">
          <div className="re-section-inner">
            <div className="re-reveal">
              <div className="re-section-eyebrow">The problem</div>
              <h2 className="re-section-title">Every agent knows <em>this</em> feeling.</h2>
            </div>
            <div className="re-pain-grid re-reveal" style={{transitionDelay:'0.1s'}}>
              <div className="re-pain-item">
                <div className="re-pain-number">01</div>
                <div className="re-pain-title">Your go-to photographer is booked.</div>
                <div className="re-pain-body">A listing hits your pipeline and your usual contact isn't available. You're back to texting people you've never worked with and hoping for the best.</div>
              </div>
              <div className="re-pain-item">
                <div className="re-pain-number" style={{position:'absolute',top:'40px',fontSize:'48px',color:'var(--border-deep)'}}>02</div>
                <div className="re-pain-title" style={{marginTop:'64px'}}>You can't vet someone from their website alone.</div>
                <div className="re-pain-body" style={{marginTop:'10px'}}>A good portfolio doesn't tell you if they'll show up on time, deliver by your MLS deadline, or handle a difficult client situation professionally.</div>
              </div>
              <div className="re-pain-item">
                <div className="re-pain-number">03</div>
                <div className="re-pain-title">Payment and contracts are still a headache.</div>
                <div className="re-pain-body">Venmo. Personal invoices. No contract. When something goes wrong — a no-show, late delivery, disputed usage rights — there's nothing to stand on.</div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="re-how-section">
          <div className="re-section-inner">
            <div className="re-reveal">
              <div className="re-section-eyebrow">How it works</div>
              <h2 className="re-section-title">From listing to <em>live</em> in four steps.</h2>
              <p className="re-section-desc">Post a project in minutes. We surface the right photographers. You pick. We handle everything else.</p>
            </div>
            <div className="re-steps-grid">
              <div className="re-step-card re-reveal re-reveal-d1">
                <div className="re-step-num re-step-num-active">1</div>
                <div className="re-step-title">Post your listing shoot</div>
                <div className="re-step-body">Describe the property, your timeline, and what you need. Takes about two minutes.</div>
              </div>
              <div className="re-step-card re-reveal re-reveal-d2">
                <div className="re-step-num">2</div>
                <div className="re-step-title">Photographers apply — or you find them</div>
                <div className="re-step-body">Qualified photographers can apply to your project, or you can browse and reach out to someone whose work you like.</div>
              </div>
              <div className="re-step-card re-reveal re-reveal-d3">
                <div className="re-step-num">3</div>
                <div className="re-step-title">Agree on the details, then book</div>
                <div className="re-step-body">Both sides confirm the scope, timeline, and rate before anything is locked in. Payment is held in escrow until you approve the work.</div>
              </div>
              <div className="re-step-card re-reveal" style={{transitionDelay:'0.4s'}}>
                <div className="re-step-num">4</div>
                <div className="re-step-title">Receive and approve your photos</div>
                <div className="re-step-body">Photos are delivered through the platform. Approve them, release payment, and your listing goes live.</div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY FRAMEFINDER */}
        <section className="re-why-section">
          <div className="re-section-inner">
            <div className="re-why-grid">
              <div className="re-why-left re-reveal">
                <div className="re-section-eyebrow">Why FrameFinder</div>
                <h2 className="re-section-title">Built for agents who need<br />someone they can <em>count on.</em></h2>
                <p className="re-section-desc" style={{marginBottom:'40px'}}>Word of mouth works until it doesn't. When your deal is on the line, you need a photographer you can count on — and a platform that backs it up.</p>
                <Link to="/signup" className="re-btn re-btn-accent re-btn">Post your first shoot free →</Link>
              </div>
              <div className="re-why-features re-reveal" style={{transitionDelay:'0.15s'}}>
                {[
                  { icon: <IconShield />, title: 'Every photographer is vetted', body: 'We review portfolios, confirm professional experience, and check references before anyone goes live on the platform. You\'re not browsing a directory — you\'re choosing from a curated bench.' },
                  { icon: <IconDoc />, title: 'Everyone agrees before anything happens', body: 'FrameFinder creates a standard project agreement so both sides are clear on scope, deliverables, timeline, and rate before the shoot is confirmed.' },
                  { icon: <IconEscrow />, title: 'Escrow protects your money', body: 'Payment is held securely until you\'ve reviewed and approved the photos. If deliverables aren\'t met, you have recourse — something a Venmo transfer can\'t give you.' },
                  { icon: <IconPin />, title: 'Denver photographers, only', body: 'Every photographer on FrameFinder is Denver-based. They know the neighborhoods, the light at different times of year, and what sells here.' },
                ].map((f, i) => (
                  <div className="re-why-feature" key={i}>
                    <div className="re-why-feature-icon">{f.icon}</div>
                    <div>
                      <div className="re-why-feature-title">{f.title}</div>
                      <div className="re-why-feature-body">{f.body}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* PHOTOGRAPHER GRID */}
        <section className="re-photographers-section">
          <div className="re-section-inner">
            <div className="re-reveal" style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',gap:'20px',flexWrap:'wrap',marginBottom:'56px'}}>
              <div>
                <div className="re-section-eyebrow">The photographers</div>
                <h2 className="re-section-title" style={{marginBottom:0}}>Denver's best <em>real estate</em> photographers.</h2>
              </div>
              <Link to="/photographers" className="re-btn re-btn-ghost">Browse all photographers →</Link>
            </div>
            <div className="re-photo-grid">
              {[
                { img: 're-photo-card-img-1', icon: <IconHouse size={36} color="#8b7a65" opacity={0.5} />, name: 'Marcus Torres', spec: <><IconStar /> 4.9 · LoDo · 47 completed shoots</>, tags: ['Real estate','Drone','Virtual tours'], rate: '$180/hr', avail: 'Available this week' },
                { img: 're-photo-card-img-2', icon: <IconHouse size={36} color="#5a7a8a" opacity={0.5} />, name: 'Priya Sharma', spec: <><IconStar /> 5.0 · Cherry Creek · 31 completed shoots</>, tags: ['Real estate','Twilight shots','HDR'], rate: '$165/hr', avail: 'Available this week' },
                { img: 're-photo-card-img-3', icon: <IconHouse size={36} color="#7a6a8a" opacity={0.5} />, name: 'Devon Wallace', spec: <><IconStar /> 4.8 · Washington Park · 22 completed shoots</>, tags: ['Real estate','Floor plans','Video walkthrough'], rate: '$150/hr', avail: 'Available tomorrow' },
              ].map((p, i) => (
                <div className={`re-photo-card re-reveal re-reveal-d${i+1}`} key={i}>
                  <div className={`re-photo-card-img ${p.img}`}>{p.icon}</div>
                  <div className="re-photo-card-body">
                    <div className="re-photo-card-name">{p.name}</div>
                    <div className="re-photo-card-spec">{p.spec}</div>
                    <div className="re-photo-card-tags">{p.tags.map((t,j) => <span className="re-tag" key={j}>{t}</span>)}</div>
                  </div>
                  <div className="re-photo-card-footer">
                    <div className="re-photo-card-rate">{p.rate}</div>
                    <div className="re-avail"><div className="re-avail-dot"></div>{p.avail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="re-cta-section">
          <div className="re-cta-inner re-reveal">
            <div className="re-cta-eyebrow">Get started · It's free during beta</div>
            <h2 className="re-cta-title">Post your first<br />listing shoot <em>today.</em></h2>
            <p className="re-cta-body">No subscription. No platform fee during beta. Post a project and get matched with a vetted Denver real estate photographer — usually within a few hours.</p>
            <div className="re-cta-acts">
              <Link to="/signup" className="re-btn re-btn-accent re-btn">Get started →</Link>
              <Link to="/photographers" className="re-btn re-btn-ghost" style={{background:'transparent',color:'rgba(255,255,255,0.5)',borderColor:'rgba(255,255,255,0.15)'}}>Browse photographers</Link>
            </div>
            <div className="re-cta-disclaimer">Free during beta. No credit card required.</div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="re-footer">
          <Link to="/" className="re-footer-logo">FrameFinder</Link>
          <div className="re-footer-links">
            <Link to="/photographers" className="re-footer-link">Photographers</Link>
            <Link to="/signup" className="re-footer-link">Get started</Link>
            <Link to="/help" className="re-footer-link">Help</Link>
          </div>
          <div className="re-footer-copy">© 2026 FrameFinder · Denver, CO</div>
        </footer>
      </div>
    </>
  );
};

export default RealEstate;
